import React, { useState, useEffect } from "react";
import axios from "axios";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
  Collapse,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  Skeleton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorPage from "../Components/ErrorPage";
import CustomerAdd from "./CustomerAdd";
import CustomerEdit from "./CustomerEdit";
import CollectPaymentDialog from "./CollectPaymentDialog";
import useDelete from "../../api/useDelete";
import { useTranslation } from "react-i18next";
import { exportCustomerReportToPDF } from "../../utils/exportToPDF";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const parseAmount = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "object") {
    return Object.values(value).reduce(
      (sum, item) => sum + (Number(item) || 0),
      0
    );
  }
  const parsed = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getTransferUnitPrice = (transfer) => {
  const raw = parseAmount(
    transfer?.source_stock_details?.retail_price ??
      transfer?.source_stock_details?.sale_price ??
      transfer?.target_stock_details?.retail_price ??
      transfer?.target_stock_details?.sale_price
  );
  return raw > 0 ? raw : 0;
};

const getTransferTotalAmount = (transfer) => {
  const qty = Number(transfer?.transfer_quantity) || 0;
  return qty * getTransferUnitPrice(transfer);
};

const getOpeningDisplay = (customer, isTransferRow) => {
  if (isTransferRow) return "0";
  if (customer.opening_balance != null && Number(customer.opening_balance) !== 0) {
    return `${Number(customer.opening_balance).toLocaleString()} ${
      customer.opening_balance_currency_details?.title || ""
    }`.trim();
  }
  return "--";
};

const getOwedDisplay = (customer, isTransferRow) => {
  if (isTransferRow) return customer.total_owed_text || "--";
  const owed = customer.total_owed_by_currency || {};
  if (Object.keys(owed).length > 0) {
    return Object.entries(owed)
      .map(([curr, amt]) => `${Number(amt).toLocaleString()} ${curr}`)
      .join(" / ");
  }
  return "--";
};

const getBranchDisplay = (customer, isTransferGroup, isAdmin) => {
  if (isTransferGroup) {
    const fromBranch = customer.shop_details?.name || "--";
    const toBranch = customer.branch_display_name || "--";
    if (isAdmin) return { fromBranch, toBranch, mode: "transfer-admin" };
    return { label: toBranch, mode: "transfer" };
  }
  return { label: customer.shop_details?.name || "--", mode: "shop" };
};

const CustomerSaleRow = ({ sale, index, onDownload }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <Button size="small" onClick={() => setOpen((v) => !v)}>
            {open ? "˄" : "˅"}
          </Button>
          {index}
        </TableCell>
        <TableCell>{sale.customer_details?.name || "-"}</TableCell>
        <TableCell>{formatDate(sale.sale_date)}</TableCell>
        <TableCell>
          {parseAmount(sale.amount) ? parseAmount(sale.amount).toLocaleString() : "-"}
        </TableCell>
        <TableCell>{parseAmount(sale.effective_total_amount ?? sale.total_amount).toLocaleString()}</TableCell>
        <TableCell>
          {sale.auto_payment_details?.account_name
            ? `${sale.auto_payment_details.account_name} (${sale.auto_payment_details.account_currency_title || "-"})`
            : "-"}
        </TableCell>
        <TableCell>{sale.auto_payment_details?.exchange_rate || "-"}</TableCell>
        <TableCell>{parseAmount(sale.total_remained_amount).toLocaleString()}</TableCell>
        <TableCell>{sale.sale_items?.length || 0}</TableCell>
        <TableCell>
          <Button size="small" variant="outlined" onClick={onDownload}>
            PDF
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={10} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Sale Items
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell><strong>Product</strong></TableCell>
                    <TableCell><strong>Quantity</strong></TableCell>
                    <TableCell><strong>Purchase</strong></TableCell>
                    <TableCell><strong>Buy Price</strong></TableCell>
                    <TableCell><strong>Wholesale Price</strong></TableCell>
                    <TableCell><strong>Retail Price</strong></TableCell>
                    <TableCell><strong>Unit</strong></TableCell>
                    <TableCell><strong>Subtotal</strong></TableCell>
                    <TableCell><strong>Profit</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(sale.sale_items || []).map((item, idx) => {
                    const qty = Number(item.quantity) || 0;
                    const salePrice = parseAmount(item.sale_price);
                    const buyPrice = parseAmount(item.unit_cost ?? item.buy_price ?? item.purchase_details?.buy_price ?? item.product_details?.buy_price);
                    const lineCost = parseAmount(item.line_cost) || qty * buyPrice;
                    const subtotal = parseAmount(item.line_total) || qty * salePrice;
                    const profit = item.line_profit !== undefined ? parseAmount(item.line_profit) : subtotal - lineCost;
                    return (
                      <TableRow key={idx}>
                        <TableCell>{item.product_details?.name || "-"}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{item.purchase_details ? `ID: ${item.purchase_details.id}` : "-"}</TableCell>
                        <TableCell>{buyPrice ? buyPrice.toLocaleString() : "-"}</TableCell>
                        <TableCell>{item.wholesale_price || item.purchase_details?.wholesale_price || item.product_details?.wholesale_price || "-"}</TableCell>
                        <TableCell>{item.retail_price || item.purchase_details?.retail_price || item.product_details?.retail_price || "-"}</TableCell>
                        <TableCell>{item.unit_details?.title || "-"}</TableCell>
                        <TableCell>{subtotal.toFixed(2)}</TableCell>
                        <TableCell>{profit.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const CustomerIndex = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const auth = useAuth();
  const currentUser = auth?.user?.user || auth?.user || {};
  const token = auth?.user?.access || auth?.user?.token || auth?.token;
  const isSuperAdmin = Boolean(
    currentUser?.is_superuser ||
      auth?.user?.user?.is_superuser ||
      auth?.user?.is_superuser
  );

  const isAdmin = Boolean(
    isSuperAdmin ||
      currentUser?.is_staff ||
      auth?.user?.user?.is_staff ||
      auth?.user?.is_staff
  );

  const [selectedBranch, setSelectedBranch] = useState(
    auth?.user?.selected_branch ? String(auth.user.selected_branch) : ""
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [openCollectPayment, setOpenCollectPayment] = useState(false);
  const [customerForPayment, setCustomerForPayment] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [openCustomerReport, setOpenCustomerReport] = useState(false);
  const [customerReportLoading, setCustomerReportLoading] = useState(false);
  const [customerReportData, setCustomerReportData] = useState(null);
  const [selectedCustomerForReport, setSelectedCustomerForReport] = useState(null);
  const [deletingTransferGroupKey, setDeletingTransferGroupKey] = useState("");
  const [expandedTransferBranches, setExpandedTransferBranches] = useState({});

  const handleDownloadReport = async () => {
    if (!token) return;
    setDownloadLoading(true);
    try {
      const base = process.env.REACT_APP_API_BASE_URL || "";
      const { data } = await axios.get(`${base}/shop/customer-report/`, {
        headers: { Authorization: `Token ${token}` },
      });
      exportCustomerReportToPDF(data);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || t("customer.reportFailed", "Failed to load report"));
    } finally {
      setDownloadLoading(false);
    }
  };

  const openCustomerReportDialog = async (customer) => {
    if (!token || !customer?.id) return;
    setSelectedCustomerForReport(customer);
    setOpenCustomerReport(true);
    setCustomerReportLoading(true);
    setCustomerReportData(null);
    try {
      const base = process.env.REACT_APP_API_BASE_URL || "";
      const params = new URLSearchParams({ customer: String(customer.id) });
      if (selectedBranch) params.set("shop", selectedBranch);
      const { data } = await axios.get(
        `${base}/shop/customer-report/?${params.toString()}`,
        { headers: { Authorization: `Token ${token}` } }
      );
      setCustomerReportData(data);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || t("customer.reportFailed", "Failed to load report"));
      setOpenCustomerReport(false);
      setSelectedCustomerForReport(null);
    } finally {
      setCustomerReportLoading(false);
    }
  };

  const handleDownloadCustomerReport = () => {
    if (customerReportData) {
      exportCustomerReportToPDF(customerReportData);
    }
  };

  const openTransferBranchReportDialog = (group) => {
    setSelectedCustomerForReport({
      id: group.rowKey,
      name: group.name,
      email: "null",
      contact: "null",
    });
    setCustomerReportData({
      customers: [
        {
          sales: group.items.map((item, index) => ({
            id: index + 1,
            customer_details: { name: item.name },
            sale_date: item.sourceTransfer?.transfer_date || "",
            amount: 0,
            effective_total_amount: Number(item.total_owed_text) || 0,
            total_paid: 0,
            total_remained_amount: Number(item.total_owed_text) || 0,
            sale_items: [
              {
                product_details: {
                  name: item.sourceTransfer?.product_details?.name || "-",
                  wholesale_price:
                    item.sourceTransfer?.source_stock_details?.wholesale_price || "-",
                  retail_price:
                    item.sourceTransfer?.source_stock_details?.retail_price ||
                    item.sourceTransfer?.source_stock_details?.sale_price ||
                    "-",
                },
                quantity: item.sourceTransfer?.transfer_quantity || 0,
                sale_price:
                  item.sourceTransfer?.source_stock_details?.retail_price ||
                  item.sourceTransfer?.source_stock_details?.sale_price ||
                  0,
                buy_price:
                  item.sourceTransfer?.source_stock_details?.buy_price || 0,
                unit_details: item.sourceTransfer?.source_stock_details?.unit || null,
                purchase_details: {
                  id: item.sourceTransfer?.source_stock_details?.id || "-",
                },
              },
            ],
            auto_payment_details: null,
          })),
        },
      ],
    });
    setOpenCustomerReport(true);
  };

  const deleteTransferBranchGroup = async (group) => {
    const transferIds = group?.items
      ?.map((item) => item?.sourceTransfer?.id)
      .filter(Boolean);
    if (!transferIds?.length) return;
    if (!window.confirm(`Delete stock transfer records for ${group.name}?`)) return;

    setDeletingTransferGroupKey(group.rowKey);
    try {
      const headers = auth?.buildAuthHeader?.() || { Authorization: `Token ${token}` };
      await Promise.all(
        transferIds.map((id) =>
          axios.delete(`${process.env.REACT_APP_API_BASE_URL}/shop/stock-transfers/${id}/`, {
            headers,
          })
        )
      );
      refetch?.();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to delete stock transfer records.");
    } finally {
      setDeletingTransferGroupKey("");
    }
  };

  const acceptTransferGroup = async (group) => {
    const pendingItems = (group?.items || []).filter(
      (item) => String(item?.sourceTransfer?.status || "").toLowerCase() === "pending"
    );
    if (!pendingItems.length) {
      alert("No pending transfers found in this group.");
      return;
    }

    try {
      for (const item of pendingItems) {
        const transfer = item?.sourceTransfer || {};
        const payload = {
          product: Number(transfer?.product?.id || transfer?.product_details?.id || 0),
          supplier_branch: Number(transfer?.supplier_branch),
          customer_branch: Number(transfer?.customer_branch),
          transfer_quantity: Number(transfer?.transfer_quantity),
          transfer_date: transfer?.transfer_date || new Date().toISOString().slice(0, 10),
          show_payment_button: Boolean(transfer?.show_payment_button),
          notes: transfer?.notes || "",
        };
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/shop/stock-transfers/${transfer.id}/accept/`,
          payload,
          {
            headers: auth?.buildAuthHeader?.() || { Authorization: `Token ${token}` },
          }
        );
      }

      refetch?.();
    } catch (error) {
      alert(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Accept transfer failed."
      );
    }
  };

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["customer", selectedBranch],
    `shop/customers/?page_size=10000${selectedBranch ? `&shop=${selectedBranch}` : ""}`,
    token
  );
  const { data: branchesData } = useFetchObjects(
    ["customer-stock-transfer-branches"],
    "shop/shop/?page_size=all&include_all=1",
    token
  );
  const { data: stockTransfersData, isLoading: isStockTransfersLoading } = useFetchObjects(
    ["customer-stock-transfers", selectedBranch],
    `shop/stock-transfers/?page_size=10000${selectedBranch ? `&supplier_branch=${selectedBranch}` : ""}`,
    token
  );
  const { data: transactionsData, refetch: refetchTransactions } = useFetchObjects(
    ["customer-transactions"],
    "shop/account-transactions/?page_size=10000",
    token
  );

  const accountTransactions = transactionsData?.results || transactionsData || [];

  const getTransfersPaidAmount = (transferIds) => {
    if (!accountTransactions || !Array.isArray(accountTransactions)) return 0;
    const tids = (transferIds || []).map((id) => String(id).trim());
    if (!tids.length) return 0;

    let totalPaid = 0;
    accountTransactions.forEach((tx) => {
      const desc = String(tx?.description || "");
      const transferMetaMatch = desc.match(/\[Transfers:\s*([^\]]+)\]/);
      const describedIds = transferMetaMatch
        ? transferMetaMatch[1].split(",").map((id) => id.trim()).filter(Boolean)
        : [];
      const matchesTransfers = describedIds.length
        ? describedIds.length === tids.length &&
          describedIds.every((id) => tids.includes(id))
        : tids.length === 1 && desc.includes(`- ${tids[0]}`);
      if (matchesTransfers) {
        totalPaid += Number(tx.amount) || 0;
      }
    });
    return totalPaid;
  };

  useEffect(() => {
    refetch();
    refetchTransactions?.();
  }, [searchTerm, page, rowsPerPage, selectedBranch, refetch, refetchTransactions]);

  const { handleDelete, ConfirmDialog } = useDelete("shop/customers", token);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleBranchChange = (e) => {
    setSelectedBranch(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    setRowsPerPage(value === "all" ? "all" : Number(value));
    setPage(1);
  };

  const customers = data && data.results ? data.results : data || [];
  const branches = branchesData?.results || branchesData || [];
  const branchNameById = branches.reduce((map, branch) => {
    map[String(branch.id)] = branch.name;
    return map;
  }, {});
  const stockTransfers = (stockTransfersData?.results || stockTransfersData || []).filter(
    (transfer) =>
      (!selectedBranch || String(transfer?.supplier_branch) === String(selectedBranch)) &&
      Boolean(transfer?.show_payment_button)
  );
  const stockTransferCustomerRows = stockTransfers.map((transfer) => {
    const branchName =
      transfer?.customer_branch_details?.name ||
      branchNameById[String(transfer?.customer_branch)] ||
      "--";
    return {
      id: branchName,
      rowKey: `transfer-${transfer.id}`,
      recordType: "stock-transfer",
      name: branchName,
      email: "null",
      contact: "null",
      address: branchName,
      opening_balance: 0,
      total_owed_text: getTransferTotalAmount(transfer).toFixed(2),
      shop: transfer?.supplier_branch || "",
      shop_details: {
        id: transfer?.supplier_branch || "",
        name:
          transfer?.supplier_branch_details?.name ||
          branchNameById[String(transfer?.supplier_branch)] ||
          "--",
      },
      branch_display_name: branchName,
      sourceTransfer: transfer,
    };
  });
  const customerRows = customers.map((customer) => ({
    ...customer,
    recordType: "customer",
  }));
  const groupedTransferRows = stockTransferCustomerRows.reduce((groups, row) => {
    const key = row.name || "--";
    if (!groups[key]) {
      groups[key] = {
        id: key,
        rowKey: `transfer-group-${key}`,
        recordType: "stock-transfer-group",
        name: key,
        email: "null",
        contact: "null",
        address: key,
        opening_balance: 0,
        total_owed_text: "0.00",
        shop: row.shop || "",
        shop_details: row.shop_details || { name: "--" },
        branch_display_name: key,
        items: [],
      };
    }
    groups[key].items.push(row);
    const totalVal = groups[key].items.reduce(
      (sum, item) => sum + (Number(item.total_owed_text) || 0),
      0
    );
    const tids = groups[key].items
      .map((item) => item.sourceTransfer?.transfer_id)
      .filter(Boolean);
    const paidVal = getTransfersPaidAmount(tids);
    groups[key].total_owed_text = Math.max(totalVal - paidVal, 0).toFixed(2);
    return groups;
  }, {});
  const mergedRows = [...customerRows, ...Object.values(groupedTransferRows)];
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredRows = normalizedSearch
    ? mergedRows.filter((row) =>
        [
          row?.name,
          row?.email,
          row?.contact,
          row?.address,
          row?.shop_details?.name,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(normalizedSearch))
      )
    : mergedRows;
  const pagedRows =
    rowsPerPage === "all"
      ? filteredRows
      : filteredRows.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalCount = filteredRows.length;
  const totalPages = rowsPerPage === "all" ? 1 : Math.ceil(totalCount / rowsPerPage);

  if (isError) {
    return <ErrorPage message={t("customer.failedToLoadCustomers")} />;
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, width: "100%", maxWidth: "100%", overflowX: "hidden" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: { xs: 1.5, md: 2 },
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2.125rem" },
              fontWeight: 700,
              color: "#102a43",
            }}
          >
            {t("customer.customerList")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            {t("customer.dataLoadedOn")} {new Date().toLocaleString()}
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <Button
            variant="outlined"
            color="primary"
            fullWidth={isMobile}
            startIcon={downloadLoading ? <CircularProgress size={18} /> : <DownloadIcon />}
            onClick={handleDownloadReport}
            disabled={downloadLoading}
            sx={{ whiteSpace: "nowrap" }}
          >
            {downloadLoading
              ? t("customer.downloading", "Downloading...")
              : t("customer.downloadReport", "Download report")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth={isMobile}
            onClick={() => setOpenAdd(true)}
            sx={{ whiteSpace: "nowrap" }}
          >
            {t("customer.addNewCustomer")}
          </Button>
        </Stack>
      </Box>

      {/* Search and Branch Filter */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={isAdmin ? 8 : 12}>
          <TextField
            variant="outlined"
            fullWidth
            size={isMobile ? "small" : "medium"}
            placeholder={t("customer.searchCustomers")}
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        {isAdmin && (
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined" size={isMobile ? "small" : "medium"}>
              <Select
                value={selectedBranch}
                onChange={handleBranchChange}
                displayEmpty
              >
                <MenuItem value="">
                  <em>{t("global.allShops", "All Shops")}</em>
                </MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={String(branch.id)}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>

      {/* Loading skeletons */}
      {(isLoading || isStockTransfersLoading) && isMobile && (
        <Stack spacing={1.5}>
          {Array.from(new Array(4)).map((_, i) => (
            <Card key={`mobile-skel-${i}`} variant="outlined">
              <CardContent>
                <Skeleton width="50%" height={28} />
                <Skeleton width="80%" />
                <Skeleton width="60%" />
                <Skeleton width="40%" />
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Mobile card list */}
      {!isLoading && !isStockTransfersLoading && isMobile && (
        <Stack spacing={1.5}>
          {pagedRows.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: "center" }}>
              <Typography color="text.secondary">
                {t("customer.noCustomers", "No customers found")}
              </Typography>
            </Paper>
          ) : (
            pagedRows.map((customer, index) => {
              const isTransferGroup = customer.recordType === "stock-transfer-group";
              const isTransferRow = isTransferGroup;
              const openingStr = getOpeningDisplay(customer, isTransferRow);
              const owedStr = getOwedDisplay(customer, isTransferRow);
              const branchInfo = getBranchDisplay(customer, isTransferGroup, isAdmin);
              const rowNo =
                rowsPerPage === "all"
                  ? index + 1
                  : (page - 1) * rowsPerPage + index + 1;

              return (
                <Card
                  key={customer.rowKey || customer.id}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: isTransferGroup ? "#90caf9" : "divider",
                    backgroundColor: isTransferGroup ? "#f6faff" : "#fff",
                    boxShadow: "0 1px 3px rgba(16,42,67,0.06)",
                  }}
                >
                  <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block" }}
                        >
                          #{rowNo}
                          {isTransferGroup ? " · Transfer" : ""}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 700,
                            color: isTransferGroup ? "#0f3f7a" : "#102a43",
                            wordBreak: "break-word",
                          }}
                        >
                          {customer.name}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={owedStr}
                        sx={{
                          fontWeight: 700,
                          maxWidth: "48%",
                          backgroundColor: "#e8f1fb",
                          color: "#12344d",
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    </Box>

                    <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                      {!isTransferRow && (
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                            {t("customer.email")}: {customer.email || "--"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t("customer.contact")}: {customer.contact || "--"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                            {t("customer.address")}: {customer.address || "--"}
                          </Typography>
                        </>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {t("customer.openingBalance", "Opening balance")}: {openingStr}
                      </Typography>
                      <Box>
                        {branchInfo.mode === "transfer-admin" ? (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              From: {branchInfo.fromBranch}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "primary.main", fontWeight: 700 }}>
                              To: {branchInfo.toBranch}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t("customer.shop")}: {branchInfo.label}
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    {isTransferGroup && (
                      <Box sx={{ mb: 1.5 }}>
                        <Button
                          size="small"
                          onClick={() =>
                            setExpandedTransferBranches((prev) => ({
                              ...prev,
                              [customer.rowKey]: !prev[customer.rowKey],
                            }))
                          }
                          endIcon={
                            expandedTransferBranches[customer.rowKey] ? (
                              <ExpandMoreIcon />
                            ) : (
                              <KeyboardArrowRightIcon />
                            )
                          }
                        >
                          {expandedTransferBranches[customer.rowKey]
                            ? t("customer.hideItems", "Hide items")
                            : t("customer.showItems", "Show items")}
                        </Button>
                        <Collapse in={Boolean(expandedTransferBranches[customer.rowKey])}>
                          <Stack spacing={1} sx={{ mt: 1 }}>
                            {(customer.items || []).map((item) => (
                              <Paper
                                key={item.rowKey}
                                variant="outlined"
                                sx={{ p: 1.25, borderRadius: 1.5, backgroundColor: "#fff" }}
                              >
                                <Typography variant="body2" fontWeight={700}>
                                  {item.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {t("customer.amountOwed", "Amount owed")}: {item.total_owed_text}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {t("customer.shop")}:{" "}
                                  {item.branch_display_name ||
                                    item.customer_branch_details?.name ||
                                    "--"}
                                </Typography>
                              </Paper>
                            ))}
                          </Stack>
                        </Collapse>
                      </Box>
                    )}

                    <Stack direction="column" spacing={1}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        fullWidth
                        onClick={() => {
                          if (isTransferRow) {
                            setCustomerForPayment({
                              ...customer,
                              id: customer.rowKey,
                              opening_balance: 0,
                              total_owed_by_currency: {
                                AFG: Number(customer.total_owed_text) || 0,
                              },
                            });
                          } else {
                            setCustomerForPayment(customer);
                          }
                          setOpenCollectPayment(true);
                        }}
                      >
                        {t("customer.collectPayment", "Collect payment")}
                      </Button>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          fullWidth
                          startIcon={<VisibilityIcon />}
                          onClick={() => {
                            if (isTransferRow) openTransferBranchReportDialog(customer);
                            else openCustomerReportDialog(customer);
                          }}
                        >
                          {t("customer.view", "View")}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          fullWidth
                          disabled={
                            isTransferRow && deletingTransferGroupKey === customer.rowKey
                          }
                          onClick={() => {
                            if (isTransferRow) deleteTransferBranchGroup(customer);
                            else handleDelete(customer.id);
                          }}
                        >
                          {isTransferRow && deletingTransferGroupKey === customer.rowKey
                            ? t("stock.deleting", "Deleting...")
                            : t("customer.delete", "Delete")}
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Stack>
      )}

      {/* Desktop table */}
      {!isMobile && (
        <TableContainer
          component={Paper}
          sx={{
            mt: 1,
            maxHeight: { md: 560, lg: 620 },
            overflow: "auto",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Table stickyHeader sx={{ minWidth: 900 }} aria-label="customer table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: "primary.main", color: "white", fontWeight: 700 }}>
                  {t("customer.id")}
                </TableCell>
                <TableCell sx={{ backgroundColor: "primary.main", color: "white", fontWeight: 700 }}>
                  {t("customer.name")}
                </TableCell>
                <TableCell sx={{ backgroundColor: "primary.main", color: "white", fontWeight: 700 }}>
                  {t("customer.email")}
                </TableCell>
                <TableCell sx={{ backgroundColor: "primary.main", color: "white", fontWeight: 700 }}>
                  {t("customer.contact")}
                </TableCell>
                <TableCell sx={{ backgroundColor: "primary.main", color: "white", fontWeight: 700 }}>
                  {t("customer.address")}
                </TableCell>
                <TableCell sx={{ backgroundColor: "primary.main", color: "white", fontWeight: 700 }}>
                  {t("customer.openingBalance", "Opening balance")}
                </TableCell>
                <TableCell sx={{ backgroundColor: "primary.main", color: "white", fontWeight: 700 }}>
                  {t("customer.amountOwed", "Amount owed")}
                </TableCell>
                <TableCell sx={{ backgroundColor: "primary.main", color: "white", fontWeight: 700 }}>
                  {t("customer.shop")}
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "primary.main",
                    color: "white",
                    fontWeight: 700,
                    minWidth: 280,
                  }}
                >
                  {t("customer.action")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || isStockTransfersLoading
                ? Array.from(new Array(5)).map((_, rowIndex) => (
                    <TableRow key={`skeleton-${rowIndex}`}>
                      {Array.from(new Array(9)).map((__, colIndex) => (
                        <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : pagedRows.map((customer, index) => {
                    const isTransferGroup = customer.recordType === "stock-transfer-group";
                    const isTransferRow = isTransferGroup;
                    const openingStr = getOpeningDisplay(customer, isTransferRow);
                    const owedStr = getOwedDisplay(customer, isTransferRow);
                    const branchInfo = getBranchDisplay(customer, isTransferGroup, isAdmin);

                    return (
                      <React.Fragment key={customer.rowKey || customer.id}>
                        <TableRow
                          hover
                          sx={
                            isTransferGroup
                              ? {
                                  backgroundColor: "#f6faff",
                                  "& td": {
                                    borderBottom: Boolean(
                                      expandedTransferBranches[customer.rowKey]
                                    )
                                      ? "none"
                                      : undefined,
                                  },
                                }
                              : undefined
                          }
                        >
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {isTransferGroup ? (
                              <Button
                                size="small"
                                variant="text"
                                sx={{
                                  minWidth: 32,
                                  mr: 1,
                                  color: "primary.main",
                                  fontWeight: 700,
                                  borderRadius: 999,
                                  px: 0.5,
                                }}
                                onClick={() =>
                                  setExpandedTransferBranches((prev) => ({
                                    ...prev,
                                    [customer.rowKey]: !prev[customer.rowKey],
                                  }))
                                }
                              >
                                {expandedTransferBranches[customer.rowKey] ? (
                                  <ExpandMoreIcon fontSize="small" />
                                ) : (
                                  <KeyboardArrowRightIcon fontSize="small" />
                                )}
                              </Button>
                            ) : null}
                            {rowsPerPage === "all"
                              ? index + 1
                              : (page - 1) * rowsPerPage + index + 1}
                          </TableCell>
                          <TableCell
                            sx={
                              isTransferGroup
                                ? { fontWeight: 700, color: "#0f3f7a" }
                                : undefined
                            }
                          >
                            {customer.name}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 180, wordBreak: "break-word" }}>
                            {customer.email}
                          </TableCell>
                          <TableCell>{customer.contact}</TableCell>
                          <TableCell sx={{ maxWidth: 160, wordBreak: "break-word" }}>
                            {customer.address}
                          </TableCell>
                          <TableCell>{openingStr}</TableCell>
                          <TableCell sx={isTransferGroup ? { fontWeight: 700 } : undefined}>
                            {owedStr}
                          </TableCell>
                          <TableCell>
                            {branchInfo.mode === "transfer-admin" ? (
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                                >
                                  From: {branchInfo.fromBranch}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "0.85rem",
                                    color: "primary.main",
                                    fontWeight: "bold",
                                  }}
                                >
                                  To: {branchInfo.toBranch}
                                </Typography>
                              </Box>
                            ) : (
                              branchInfo.label
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => {
                                  if (isTransferRow) {
                                    setCustomerForPayment({
                                      ...customer,
                                      id: customer.rowKey,
                                      opening_balance: 0,
                                      total_owed_by_currency: {
                                        AFG: Number(customer.total_owed_text) || 0,
                                      },
                                    });
                                  } else {
                                    setCustomerForPayment(customer);
                                  }
                                  setOpenCollectPayment(true);
                                }}
                              >
                                {t("customer.collectPayment", "Collect payment")}
                              </Button>
                              <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<VisibilityIcon />}
                                size="small"
                                onClick={() => {
                                  if (isTransferRow) openTransferBranchReportDialog(customer);
                                  else openCustomerReportDialog(customer);
                                }}
                              >
                                {t("customer.view", "View")}
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                disabled={
                                  isTransferRow &&
                                  deletingTransferGroupKey === customer.rowKey
                                }
                                onClick={() => {
                                  if (isTransferRow) deleteTransferBranchGroup(customer);
                                  else handleDelete(customer.id);
                                }}
                              >
                                {isTransferRow &&
                                deletingTransferGroupKey === customer.rowKey
                                  ? t("stock.deleting", "Deleting...")
                                  : t("customer.delete", "Delete")}
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                        {isTransferGroup && (
                          <TableRow>
                            <TableCell
                              colSpan={9}
                              sx={{
                                py: 0,
                                px: 0,
                                borderBottom: "1px solid rgba(25, 118, 210, 0.12)",
                                backgroundColor: "#fbfdff",
                              }}
                            >
                              <Collapse
                                in={Boolean(expandedTransferBranches[customer.rowKey])}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box sx={{ p: 2.5 }}>
                                  <Typography
                                    variant="subtitle2"
                                    gutterBottom
                                    sx={{ fontWeight: 700, color: "#0f3f7a", mb: 1.5 }}
                                  >
                                    Stock Transfer Items
                                  </Typography>
                                  <TableContainer sx={{ overflowX: "auto" }}>
                                    <Table
                                      size="small"
                                      sx={{
                                        border: "1px solid rgba(25, 118, 210, 0.12)",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                        backgroundColor: "#fff",
                                        minWidth: 640,
                                      }}
                                    >
                                      <TableHead>
                                        <TableRow sx={{ backgroundColor: "#eef5ff" }}>
                                          <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                                          <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                                          <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                                          <TableCell sx={{ fontWeight: 700 }}>Address</TableCell>
                                          <TableCell sx={{ fontWeight: 700 }}>
                                            Opening balance (existing dues)
                                          </TableCell>
                                          <TableCell sx={{ fontWeight: 700 }}>Amount owed</TableCell>
                                          <TableCell sx={{ fontWeight: 700 }}>Branch</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {customer.items.map((item) => (
                                          <TableRow key={item.rowKey}>
                                            <TableCell>{item.name}</TableCell>
                                            <TableCell>{item.email}</TableCell>
                                            <TableCell>{item.contact}</TableCell>
                                            <TableCell>{item.address}</TableCell>
                                            <TableCell>0</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>
                                              {item.total_owed_text}
                                            </TableCell>
                                            <TableCell>
                                              {item.branch_display_name ||
                                                item.customer_branch_details?.name ||
                                                "--"}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 1.5, sm: 2 },
          py: 2,
          px: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2">{t("customer.rowsPerPage")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": { border: "none" },
              },
            }}
            size="small"
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {rowsPerPage !== "all" && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            color="primary"
            size={isMobile ? "small" : "medium"}
            siblingCount={isMobile ? 0 : 1}
          />
        )}
      </Box>

      {openAdd && (
        <CustomerAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
          onSuccess={refetch}
        />
      )}
      {openEdit && (
        <CustomerEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
          onSuccess={refetch}
        />
      )}

      {openCollectPayment && (
        <CollectPaymentDialog
          open={openCollectPayment}
          onClose={() => {
            setOpenCollectPayment(false);
            setCustomerForPayment(null);
          }}
          customer={customerForPayment}
          token={token}
          onSuccess={() => {
            refetch();
            refetchTransactions?.();
          }}
        />
      )}

      <Dialog
        open={openCustomerReport}
        onClose={() => {
          setOpenCustomerReport(false);
          setSelectedCustomerForReport(null);
          setCustomerReportData(null);
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {selectedCustomerForReport?.name || t("customer.view", "Customer")} {t("customer.report", "Report")}
        </DialogTitle>
        <DialogContent dividers>
          {customerReportLoading ? (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              {customerReportData?.customers?.[0] ? (
                <>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {selectedCustomerForReport?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedCustomerForReport?.email || "--"} | {selectedCustomerForReport?.contact || "--"}
                    </Typography>
                  </Box>
                  <Divider />
                  <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader aria-label="customer sales table">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("sale.id", "ID")}</TableCell>
                          <TableCell>{t("sale.customer", "Customer")}</TableCell>
                          <TableCell>{t("sale.saleDate", "Sale Date")}</TableCell>
                          <TableCell>{t("sale.discountAmount", "Discount Amount")}</TableCell>
                          <TableCell>{t("sale.totalAmount", "Total Amount")}</TableCell>
                          <TableCell>{t("sale.account", "Account")}</TableCell>
                          <TableCell>{t("sale.exchangeRate", "Exchange Rate")}</TableCell>
                          <TableCell>{t("sale.totalRemained", "Total Remained")}</TableCell>
                          <TableCell>{t("sale.itemCount", "Item Count")}</TableCell>
                          <TableCell>{t("sale.action", "Action")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customerReportData.customers[0].sales?.length > 0 ? (
                          customerReportData.customers[0].sales.map((sale, idx) => (
                            <CustomerSaleRow
                              key={sale.id}
                              sale={sale}
                              index={idx + 1}
                              onDownload={handleDownloadCustomerReport}
                            />
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={10} align="center">
                              {t("customer.noSales", "No sales found")}
                            </TableCell>
                          </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {t("customer.balancePayments", "Balance Payments")}
                  </Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 280 }}>
                    <Table size="small" stickyHeader aria-label="customer balance payments table">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("sale.id", "ID")}</TableCell>
                          <TableCell>{t("sale.saleDate", "Payment Date")}</TableCell>
                          <TableCell>{t("sale.account", "Account")}</TableCell>
                          <TableCell>{t("sale.amount", "Amount")}</TableCell>
                          <TableCell>{t("sale.currency", "Currency")}</TableCell>
                          <TableCell>{t("sale.exchangeRate", "Exchange Rate")}</TableCell>
                          <TableCell>{t("sale.totalPaid", "Converted")}</TableCell>
                          <TableCell>{t("sale.notes", "Notes")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customerReportData.customers[0].balance_payments?.length > 0 ? (
                          customerReportData.customers[0].balance_payments.map((payment, idx) => (
                            <TableRow key={payment.id || idx} hover>
                              <TableCell>{payment.id || idx + 1}</TableCell>
                              <TableCell>{formatDate(payment.payment_date)}</TableCell>
                              <TableCell>
                                {payment.account_details?.name
                                  ? `${payment.account_details.name} (${payment.account_details.currency_title || "-"})`
                                  : "-"}
                              </TableCell>
                              <TableCell>{parseAmount(payment.amount).toLocaleString()}</TableCell>
                              <TableCell>{payment.currency_details?.title || "-"}</TableCell>
                              <TableCell>{payment.exchange_rate || "-"}</TableCell>
                              <TableCell>
                                {parseAmount(payment.converted_amount || payment.amount).toLocaleString()}
                              </TableCell>
                              <TableCell>{payment.notes || "-"}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              {t("customer.noBalancePayments", "No balance payments found")}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Typography>{t("customer.noReportData", "No report data available")}</Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDownloadCustomerReport}
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={!customerReportData || customerReportLoading}
          >
            {t("customer.downloadPdf", "Download PDF")}
          </Button>
          <Button
            onClick={() => {
              setOpenCustomerReport(false);
              setSelectedCustomerForReport(null);
              setCustomerReportData(null);
            }}
          >
            {t("common.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog />
    </Box>
  );
};

export default CustomerIndex;
